/**
 * Strategy List Selector Registry
 *
 * Single source of truth for ALL strategy list/form/canvas data-testid values
 * used in strategy-related E2E test helpers.
 *
 * Source components:
 * - frontend/src/components/ProjectView/ProjectViewPage.jsx
 * - src/modules/strategy-blocks/components/StrategiesListView.tsx
 * - src/modules/strategy-blocks/components/StrategyFormDialog.tsx
 * - src/modules/reactFlow/components/shared/TopControlPanel.tsx
 */
export const STRATEGY_LIST = {
  // ── Tab ──
  tab: 'project-tab-strategies',

  // ── List ──
  table: 'strategies-list-table',
  createBtn: 'strategies-create-btn',
  /** Template: `strategy-row-${id}` */
  row: (id: string) => `strategy-row-${id}`,
  /** Template: `strategy-copy-link-${id}` */
  copyLink: (id: string) => `strategy-copy-link-${id}`,
  /** Template: `strategy-open-tab-${id}` */
  openTab: (id: string) => `strategy-open-tab-${id}`,
  /** Template: `strategy-actions-btn-${id}` */
  actionsBtn: (id: string) => `strategy-actions-btn-${id}`,

  // ── Menu items ──
  menuEdit: 'strategy-menu-edit',
  menuClone: 'strategy-menu-clone',
  menuDelete: 'strategy-menu-delete',
  menuOpenCanvas: 'strategy-menu-open-canvas',

  // ── Form dialog sections (SAccordion roots) ──
  formSectionSourceData: 'strategy-form-section-source-data',

  // ── Form dialog ──
  formDialog: 'strategy-form-dialog',
  formName: 'strategy-form-name',
  formType: 'strategy-form-type',
  formDescription: 'strategy-form-description',
  formToneOfVoice: 'strategy-form-tone-of-voice',
  formDesignDescription: 'strategy-form-design-description',
  formAIModel: 'strategy-form-ai-model',
  formAIMaxTokens: 'strategy-form-ai-max-tokens',
  formGenerateScreens: 'strategy-form-generate-screens',

  // ── SG-A (T7b): persisted block generation mode ──
  /** The «Block generation» control group inside the create-mode «Generation» accordion. */
  formBlockGenerationMode: 'strategy-form-block-generation-mode',
  /** Template: `strategy-form-block-generation-mode-${parallel|sequential_by_branch}` */
  formBlockGenerationModeOption: (mode: string) => `strategy-form-block-generation-mode-${mode}`,
  /** Read-only readback of the SAVED mode in the «Generate blocks with AI» confirm (edit mode). */
  formGenerateConfirmMode: 'strategy-form-generate-confirm-mode',
  formSaveBtn: 'strategy-form-save-btn',
  formSaveWithoutAIBtn: 'strategy-form-save-without-ai-btn',

  // ── Building blocks section (Block Examples Library, Phase 3) ──
  /** «Available blocks» whitelist section (chip grid, and the wizard's step-7 tables). */
  availableBlocksSection: 'strategy-available-blocks-section',

  buildingBlocksSection: 'building-blocks-section',
  buildingBlocksToggle: 'building-blocks-toggle',
  buildingBlocksSearch: 'building-blocks-search',
  /**
   * Template: `building-blocks-item-${entryKey}`.
   *
   * The argument is the example's TIER-AWARE key — `<source>:<exampleId>`, e.g.
   * `project:credible-next-step-bridge` — never the bare document id: the same
   * id can exist in BOTH tiers and the two rows must stay addressable apart
   * (D-LIVE2-1 / F-LIVE2-5).
   */
  buildingBlocksItem: (id: string) => `building-blocks-item-${id}`,
  /** Template: `building-blocks-select-${entryKey}` (checkbox wrapper; assert the inner input). Key shape as above. */
  buildingBlocksSelect: (id: string) => `building-blocks-select-${id}`,
  /** Template: `building-blocks-mode-${entryKey}-${include|adapt|pattern}`. Key shape as above. */
  buildingBlocksMode: (id: string, mode: string) => `building-blocks-mode-${id}-${mode}`,
  buildingBlocksPreview: 'building-blocks-preview',
  buildingBlocksCapsCounter: 'building-blocks-caps-counter',

  // ── Canvas ──
  canvasCloseBtn: 'close-button',
  // ── Data Picker (CompositeDataPicker inside form) ──
  picker: 'composite-data-picker',
  pickerTabs: 'composite-picker-tabs',
  pickerTab: (type: string) => `composite-picker-tab-${type}`,
  pickerPanel: (type: string) => `composite-picker-panel-${type}`,
  /** Vertical (guided) composite layout root — used by the create-strategy dialog. */
  pickerVertical: 'composite-data-picker-vertical',
  /** Template: `composite-picker-section-${type}` (vertical layout only) */
  pickerSection: (type: string) => `composite-picker-section-${type}`,
  /**
   * Template: `composite-picker-caption-${type}` — the consumer-supplied header
   * caption of a vertical section (e.g. the Variables «N selected · G global +
   * T task-scoped» summary, computed from the ORCHESTRATOR selection).
   */
  pickerCaption: (type: string) => `composite-picker-caption-${type}`,

  // ── Intake readiness strip (strategy-intake-feasibility, Part B) ──
  // Registered ahead of the components that render them (Tasks 6-11 of the
  // 2026-09-03 strategy-intake-feasibility plan) so specs never hardcode ids.
  readinessStrip: 'strategy-readiness-strip',
  readinessStatus: 'strategy-readiness-status',
  /** Template: `strategy-readiness-blocker-${blockerId}` */
  readinessBlockerPrefix: 'strategy-readiness-blocker-',
  /**
   * The blocker's one-click fix. «Select all N routes» in the strip layout
   * (edit mode), «Select all N tasks» in the create wizard's rows layout.
   */
  readinessFix: 'strategy-readiness-fix',
  /**
   * Template: `strategy-readiness-fix-${scopeId}` — the fix of a blocker that
   * can appear SEVERAL times in one verdict. W13: `task_without_theses` is
   * emitted once per starved scope, so two starved tasks render two fixes; the
   * bare `readinessFix` above stays the single routes fix (`select_routes`),
   * which can only ever appear once.
   */
  readinessFixScope: (scopeId: string) => `strategy-readiness-fix-${scopeId}`,
  /** «Adjust in Source data» — strip layout only (the rows layout has per-row Edit). */
  readinessAdjust: 'strategy-readiness-adjust',
  /**
   * One fact cell: `routes` | `variables` | `social-proof` | `analysis`, plus
   * `insight-variables` in the create wizard's rows layout (the strip folds
   * that ratio into the `variables` sub-line). `readinessFactValue` is the
   * number inside the same cell.
   */
  readinessFact: (key: string) => `strategy-readiness-fact-${key}`,
  readinessFactValue: (key: string) => `strategy-readiness-fact-${key}-value`,
  /** Template: `strategy-readiness-warning-${warningCode}` (never gates the primary). */
  readinessWarningPrefix: 'strategy-readiness-warning-',
  /** Info row: task-scoped variables return when routes are selected. */
  readinessTaskScopedNote: 'strategy-readiness-task-scoped-note',
  /** The ONE footer helper line naming why «Create with AI» is disabled. */
  formDisabledReason: 'strategy-form-disabled-reason',

  // ── Insight-impact dialog (Spec C/D; quote sub-rows: W13 DM2) ──
  insightImpactDialog: 'insight-impact-dialog',
  /** Template: `insight-impact-row-${insightId}` — one impacted-insight chain. */
  insightImpactRow: (insightId: string) => `insight-impact-row-${insightId}`,
  /**
   * Template: `insight-impact-quote-${insightId}-${sourceItemId}` — the quote
   * sub-row of a chain, rendered ONLY when the server index carries the item id
   * for that citation (AC-W13-28). A thesis-only chain renders none.
   */
  insightImpactQuote: (insightId: string, sourceItemId: string) =>
    `insight-impact-quote-${insightId}-${sourceItemId}`,

  // ── Insight-first intake controls (strategy-intake-feasibility, Part B) ──
  insightAnalysisSelect: 'strategy-insight-analysis-select',
  /** Template: `strategy-insight-analysis-item-${analysisId}` (Select MenuItem). */
  insightAnalysisItem: (analysisId: string) => `strategy-insight-analysis-item-${analysisId}`,
  sourceInsightVariables: 'strategy-source-insight-variables',
  /** AC-021 empty state action: «Review variables in Insights». */
  sourceInsightVariablesReview: 'strategy-source-insight-variables-review',
  /** «Insight-derived variables» generation Select (create mode). */
  insightVariableGenerationSelect: 'strategy-form-insight-variable-generation',
  variablesFollowTasksSwitch: 'strategy-variables-follow-tasks',
  /**
   * Read-only «what will be cloned» summary of the strategy variables. Since
   * W12 Part B it lives on the create wizard's review step (chips, the
   * «Need review: …» caption and the empty-project line).
   */
  variablesGuidanceSummary: 'strategy-variables-guidance-summary',
  /** «Variable package» summary block and its two count chips (create mode). */
  variablePackageSummary: 'strategy-variable-package-summary',
  variablePackageProductCount: 'strategy-variable-package-product-count',
  variablePackageInsightCount: 'strategy-variable-package-insight-count',
  /**
   * Why the variable package blocks «Create with AI». Since W12 Part B it is the
   * caption row above the insight-variables table, carrying the «Review
   * variables in Insights» escape (it was an Alert inside the source-data
   * accordion before).
   */
  variablePackageBlockingReason: 'strategy-variable-package-blocking-reason',

  // ── Create-strategy wizard (W12 Part B, Task B1) ──
  /** Wizard shell root (create mode only). */
  wizard: 'strategy-create-wizard',
  /** Template: `strategy-wizard-step-${key}` — one rail StepButton per step. */
  wizardRailStep: (key: string) => `strategy-wizard-step-${key}`,
  /** The active step's `h5` title. */
  wizardStepTitle: 'strategy-wizard-step-title',
  /** The ⓘ next to the step title («About this step») — opens the step guide. */
  wizardStepHelp: 'strategy-wizard-step-help',
  /** First-run vocabulary note and its «Got it» dismissal. */
  wizardFirstRunNote: 'strategy-wizard-first-run-note',
  wizardFirstRunGotIt: 'strategy-wizard-first-run-got-it',
  /**
   * The rail's own container — a STICKY bar under the dialog header (W12 Task
   * B9d): only the step body scrolls, so the spine never leaves the viewport.
   * Below ~1080px it becomes its own horizontally scrollable strip.
   */
  wizardRail: 'strategy-wizard-rail',
  /**
   * The horizontally scrolling STRIP inside the rail (W13 IS2, REVIEW tier 2
   * MF-4). `wizardRail` is the sticky wrapper around it, which also carries the
   * two edge fades below.
   */
  wizardRailStrip: 'strategy-wizard-rail-strip',
  /**
   * The rail's edge fades. Rendered ONLY while the step track overflows its
   * strip, so their presence IS the assertion «this rail scrolls» — at wide
   * layouts neither exists.
   */
  wizardRailFadeStart: 'strategy-wizard-rail-fade-start',
  wizardRailFadeEnd: 'strategy-wizard-rail-fade-end',
  /**
   * The footer's own container — STICKY at the dialog bottom (W12 Task B9d), so
   * the primary and the one helper line under it are always in the viewport.
   * Shared by the wizard steps, the review step and the quick-create screen.
   */
  wizardFooter: 'strategy-wizard-footer',
  /** Footer controls. The review step reuses `formSaveBtn` / `formSaveWithoutAIBtn`. */
  wizardBack: 'strategy-wizard-back',
  wizardSkipToReview: 'strategy-wizard-skip-to-review',
  wizardNext: 'strategy-wizard-next',
  /** Header link that swaps guided ⇄ quick create. */
  wizardSwitchMode: 'strategy-wizard-switch-mode',

  // ── Create-wizard selection tables (W12 Part B, Task B3) ──
  /**
   * One `SelectionTable` per selection step. `plane` is the STEP key the table
   * belongs to: `tasks` | `insights` | `insightVars` | `projectVars` | `proof`.
   * `insights` is the W13 thesis table — its rows are the theses of the pinned
   * analysis, never the insight-derived VARIABLES (`insightVars`), which are a
   * different step with different ids.
   */
  wizardTable: (plane: string) => `strategy-wizard-table-${plane}`,
  /** One row of a selection table, keyed by the entity id it selects. */
  wizardTableRow: (plane: string, id: string) => `strategy-wizard-table-${plane}-row-${id}`,
  /**
   * The head cell hosting the inline search — rendered ONLY when the table has
   * ten rows or more (owner ruling: search appears from ten rows).
   */
  wizardTableSearch: (plane: string) => `strategy-wizard-table-${plane}-search`,
  /** The footer caption under a selection table («3 of 3 selected · …»). */
  wizardTableSummary: (plane: string) => `strategy-wizard-table-${plane}-summary`,
  /**
   * The explanatory sentence under a selection table's summary — the library
   * step's «… open ⓘ to see its screens …» (approved canvas v3.3, restored by
   * Task B9d). Rendered only when the caller passes one.
   */
  wizardTableFooter: (plane: string) => `strategy-wizard-table-${plane}-footer`,

  // ── Create-wizard steps 7-8: block types and library examples (Task B5) ──
  /**
   * The two `SelectionTable`s of the «Block types» step: the ten built-in block
   * types and the project's own subtypes. Rows keep the `wizardTableRow`
   * convention (`…-row-<blockType|slug>`).
   */
  wizardBlockTypesTable: 'strategy-wizard-table-blockTypes',
  wizardSubtypesTable: 'strategy-wizard-table-subtypes',
  /**
   * The library-examples table of the «Building blocks» step. Rows are keyed by
   * the TIER-AWARE key `<source>:<exampleId>`, so a row testid reads
   * `strategy-wizard-table-library-row-project:<exampleId>` (D-LIVE2-1).
   */
  wizardLibraryTable: 'strategy-wizard-table-library',
  /** The example details SWAPPED INTO the step — never a second dialog (invariant 18). */
  wizardLibraryDetails: 'strategy-wizard-library-details',
  /** «← Back to the examples» — returns to the table with the selection intact. */
  wizardLibraryDetailsBack: 'strategy-wizard-library-details-back',
  /**
   * Template: `strategy-wizard-library-row-info-${entryKey}` — the row's ⓘ.
   * The argument is the tier-aware key `<source>:<exampleId>`, never a bare id.
   */
  wizardLibraryRowInfo: (id: string) => `strategy-wizard-library-row-info-${id}`,
  /**
   * SPEC-019 conflict Alert on the «Building blocks» step: a materialized
   * example whose block type the step-7 whitelist excludes. The same conflict
   * gates «Create with AI» (one reason line in the footer) and paints the
   * `library` rail step `error`.
   */
  wizardLibraryConflict: 'strategy-library-availability-conflict',

  // ── Create-wizard step «Insights» (W13 IS2) ──
  /**
   * The inline refusal on the «Insights» step: the selection left a scope the
   * step DISPLAYS with zero theses. It names the scope (the global package or a
   * task by name), paints the rail step `error` and gates «Create with AI» with
   * the same sentence the server's `task_without_theses` blocker prints
   * (AC-W13-04). Absent while every displayed scope keeps a thesis.
   */
  wizardInsightsStarved: 'strategy-wizard-insights-starved',
  /**
   * Template: `strategy-wizard-insights-starved-${scopeId}` — ONE row of that
   * refusal, keyed by the scope it is about (`global` or the task id), so two
   * starved tasks stay individually addressable.
   */
  wizardInsightsStarvedScope: (scopeId: string) => `strategy-wizard-insights-starved-${scopeId}`,
  /**
   * Template: `strategy-wizard-insights-starved-fix-${scopeId}` — the CONTAINED
   * fix inside that refusal row («Select all theses of "Build muscle"»). Its
   * twin on the review step is the readiness panel's `readinessFix`
   * (designer invariant 26: a refusal is never a dead end).
   */
  wizardInsightsStarvedFix: (scopeId: string) => `strategy-wizard-insights-starved-fix-${scopeId}`,
  /**
   * Template: `strategy-wizard-insights-scope-more-${thesisId}` — the «+N» chip
   * in a thesis row's Scope cell when the thesis belongs to more than one task
   * (REVIEW tier 2 round 2, MF-1: the cell shows the first task and folds the
   * rest). Its `title` carries every scope, « · »-joined. Absent on rows with
   * one scope.
   */
  wizardInsightsScopeMore: (thesisId: string) => `strategy-wizard-insights-scope-more-${thesisId}`,
  /**
   * «Include actor quotes» on the AI-settings step (W13 ruling R1: OFF by
   * default). ON sends `insightSelection.includeSources: true`, which prints the
   * actors' own words under each thesis.
   */
  wizardInsightQuotes: 'strategy-wizard-insight-quotes',

  // ── Create-wizard steps 9-10: AI settings and review (Task B6) ──
  /**
   * «Advanced settings» on the AI-settings step — swaps the Max-output-tokens
   * view INTO the step (owner ruling: never a second dialog).
   */
  wizardAdvancedSettings: 'strategy-wizard-advanced-settings',
  /** «← Back to AI settings» — leaves the Advanced view with the value intact. */
  wizardAdvancedBack: 'strategy-wizard-advanced-back',
  /** The two «What you get» estimate rows of the review step (always labelled *estimated*). */
  wizardReviewScreensRow: 'strategy-wizard-review-screens',
  wizardReviewCostRow: 'strategy-wizard-review-cost',
  /**
   * Template: `strategy-wizard-review-also-sent-${key}` — one «Also sent» row
   * (`insights` | `details` | `blockTypes` | `library` | `generation`).
   */
  wizardReviewAlsoSent: (key: string) => `strategy-wizard-review-also-sent-${key}`,
  /**
   * Template: `strategy-wizard-review-edit-${key}` — the «Edit» of a review row.
   * `key` is a readiness fact (`analysis` | `routes` | `insights` |
   * `insight-variables` | `variables` | `social-proof`) or an «Also sent» key;
   * both jump to the step that owns the value.
   */
  wizardReviewEdit: (key: string) => `strategy-wizard-review-edit-${key}`,
  /**
   * F-LIVE2-40: the review step's honest PREVIEW of the server gate that
   * refuses `adapt` + screens OFF. A warning, never a second disabled state
   * (ruling FW9-R1) — the server refusal stays the gate.
   */
  wizardAdaptScreensWarning: 'strategy-wizard-adapt-screens-warning',
  /**
   * F-LIVE2-40: the review step's inline surface for a REFUSED submit. Replaces
   * the raw `alert()` the create path used to speak through.
   */
  wizardSubmitError: 'strategy-wizard-submit-error',

  // ── Create-mode quick create (W12 Part B, Task B7) ──
  /**
   * The one-screen repeat path (create mode, quick). It replaces the wizard
   * shell entirely — `wizard` is absent while it is on screen — and the header
   * link that swaps back is the shared `wizardSwitchMode`.
   */
  quickCreate: 'strategy-quick-create',
  /**
   * Template: `strategy-quick-create-row-${key}` — one settings row. `key` is a
   * wizard step key without `analysis`/`review` (`tasks` | `insights` |
   * `insightVars` | `projectVars` | `proof` | `details` | `blockTypes` |
   * `library` | `generation`), plus `screens` (which opens the review step).
   */
  quickCreateRow: (key: string) => `strategy-quick-create-row-${key}`,
  /** Template: `strategy-quick-create-change-${key}` — the row's «Change». */
  quickCreateChange: (key: string) => `strategy-quick-create-change-${key}`,
  /** The readiness chip next to «Settings» (same verdict as the review status). */
  quickCreateReady: 'strategy-quick-create-ready',

  // ── Picker state testids (per picker type) ──
  /** Template: `{type}-picker-tree` / `{type}-picker-empty` / `{type}-picker-loading` / `{type}-picker-error` */
  pickerTree: (type: string) => `${type}-picker-tree`,
  pickerEmpty: (type: string) => `${type}-picker-empty`,
  pickerLoading: (type: string) => `${type}-picker-loading`,
  pickerError: (type: string) => `${type}-picker-error`,
} as const;
